import { animated, useSpring } from '@react-spring/web';
import { useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import Tooltip from 'components/layout/tooltip';
import { useMemo } from 'react';
import { getAccuracyLevel } from 'utils/sizing';
import { generateStructureAccuracyData } from 'utils/ui';

export default function SizingAccuracyRing() {
  const { structure, structureTypeId, existingSystems } = useAppSelector(
    (state) => state.structure
  );
  const { projectSettings, propertyLocation } = useAppSelector(
    (state) => state.project
  );
  const defaultSizingItemsVersion = useAppSelector(
    (state) => state.data.defaultSizingItemsVersion
  );
  const { manuallyCompletedSteps } = useAppSelector((state) => state.ui);
  const ringSize = 80;
  const ringStrokeWidth = 6;
  const ringRadius = (ringSize - ringStrokeWidth) / 2;
  const ringCircumference = ringRadius * 2 * Math.PI;

  const accuracyData = useMemo(() => {
    return generateStructureAccuracyData();
  }, [
    JSON.stringify(
      structure.map((level) => ({
        conditioned: level.conditioned,
        attributes: level.attributes,
        electricalPanels: level.electricalPanels,
        sections: level.sections.map((section) => ({
          spaces: section.spaces.map((space) => ({
            attributes: space.attributes,
            sizingAttributes: space.sizingAttributes
          }))
        }))
      }))
    ),
    JSON.stringify(projectSettings),
    structureTypeId,
    defaultSizingItemsVersion,
    propertyLocation?.lat,
    propertyLocation?.lng,
    manuallyCompletedSteps,
    existingSystems
  ]);

  const animatedProps = useSpring({
    progress: accuracyData.overallAccuracy,
    config: { tension: 120, friction: 14 }
  });

  const accuracyLevel = getAccuracyLevel(accuracyData.overallAccuracy);

  return (
    <div className='relative cursor-help'>
      <div className='relative flex items-center justify-center'>
        <svg width={ringSize} height={ringSize} className='-rotate-90'>
          <circle
            cx={ringSize / 2}
            cy={ringSize / 2}
            r={ringRadius}
            fill='none'
            stroke='#e5e7eb'
            strokeWidth={ringStrokeWidth}
          />
          <animated.circle
            cx={ringSize / 2}
            cy={ringSize / 2}
            r={ringRadius}
            fill='none'
            stroke='url(#accuracyGradient)'
            strokeWidth={ringStrokeWidth}
            strokeLinecap='round'
            strokeDasharray={ringCircumference}
            strokeDashoffset={animatedProps.progress.to(
              (p) => ringCircumference - (p / 100) * ringCircumference
            )}
          />
          <defs>
            <linearGradient
              id='accuracyGradient'
              x1='0%'
              y1='0%'
              x2='100%'
              y2='0%'
            >
              <stop offset='0%' stopColor='#10b981' />
              <stop offset='100%' stopColor='#059669' />
            </linearGradient>
          </defs>
        </svg>
        <div className='absolute inset-0 flex flex-col items-center justify-center'>
          <animated.span className='text-xl font-bold text-gray-800'>
            {animatedProps.progress.to((p) => `${Math.round(p)}%`)}
          </animated.span>
          <span className={clsx('text-sm font-medium', accuracyLevel.color)}>
            {accuracyLevel.label}
          </span>
        </div>
      </div>
      <Tooltip text='Reflects how complete your structure details are. The more information you provide, the more accurate your sizing estimate will be.' />
    </div>
  );
}
